import React, { useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Divider,
  makeStyles,
  Step,
  StepLabel,
  Stepper
} from '@material-ui/core';
import { SelectUser } from '../common/SelectUser';
import { UserModel } from '../../UsersTable';
import { SelectAmount } from './SelectAmount';
import { SelectSubscription, SubscriptionChange } from './SelectSubscription';
import { ConfirmCheck } from './ConfirmCheck';
import Fetcher from '../../../services/fetcher';
import settings from '../../../settings';
import { useNotification } from '../../../contexts/Notification';

export interface Check {
  amount: string,
  checkId: string,
  creationDate: string,
  email: string,
  userId: string
}

interface UserModalProps extends DialogProps {
  onRequestClose: (newCheck: Check) => void,
  onSubscriptionUpdate: () => void
}

const useStyle = makeStyles((theme) => ({
  divider: {
    marginBottom: theme.spacing(2)
  }
}));

export const CheckCreationModal = (props: UserModalProps) => {
  const classes = useStyle();
  const [stepIndex, setStepIndex] = useState<number>(0);
  const [user, setUser] = useState<UserModel | null>(null);
  const [amount, setAmount] = useState<string>('');
  const [subscription, setSubscription] = useState<SubscriptionChange | null>(null);
  const [subscriptionSkipped, setSubscriptionSkipped] = useState<boolean>(false);
  const notification = useNotification();

  useEffect(() => {
    setSubscription(null);
  }, [user]);

  const addCheck = (): void => {
    const proms: Promise<any>[] = [];
    if (!!user) {
      const addCheck = Fetcher.post(settings.PaymentProvider.paymentApi + '/check', JSON.stringify({
        userId: user.id.toString(),
        email: user.email,
        amount: (Number(amount) * 100).toString(),
        subscription: subscriptionSkipped ? null : subscription
      }));
      proms.push(addCheck);
      if (!subscriptionSkipped && !!subscription) {
        const updateSubs = Fetcher.post(settings.PaymentProvider.paymentApi + '/user-sub', JSON.stringify({
          userId: user.id.toString(),
          role: subscription.role,
          endDate: (subscription.endDate.getTime() / 1000) | 0,
          active: true,
          paymentWay: 'check'
        }));
        proms.push(updateSubs);
      }
    };
    Promise.all(proms).then((res: any[]) => {
      if (res.length === 2)
        props.onSubscriptionUpdate();
      props.onRequestClose(res[0] as Check);
    }).catch((e: Error) => {
      notification.notify(e.message, "error");
      console.error(e);
    });
  }

  const getStepContent = () => {
    switch (stepIndex) {
      case 0:
        return (<SelectUser onUserSelected={(user: UserModel | null) => setUser(user)} default={user} displayHelper label="Adresse e-mail de l'utilisateur cible"/>);
      case 1:
        return (<SelectAmount onAmountChange={(givenAmount: string) => setAmount(givenAmount)} default={amount}/>);
      case 2:
        return (<SelectSubscription onSubscriptionChange={(givenSub: SubscriptionChange) => setSubscription(givenSub)} default={subscription} user={user as UserModel}/>);
      default:
        return (<ConfirmCheck user={user as UserModel} amount={amount} subscription={subscription as SubscriptionChange} subscriptionSkipped={subscriptionSkipped}/>);
    }
  }

  const handlePrev = () => {
    if (stepIndex > 0)
      setStepIndex((prevIdx: number) => prevIdx - 1);
  };

  const handleNext = () => {
    if (stepIndex === 3)
      addCheck();
    if (stepIndex === 2)
      setSubscriptionSkipped(false);
    setStepIndex((prevIdx: number) => prevIdx + 1);
    // props.onRequestClose(true); terminé
  };

  return (
    <Dialog open={props.open} onClose={props.onClose} maxWidth={false}>
      <DialogTitle>
        Enregistrement d'un nouveau chèque.
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Stepper activeStep={stepIndex}>
          <Step>
            <StepLabel>Utilisateur</StepLabel>
          </Step>
          <Step>
            <StepLabel>Montant</StepLabel>
          </Step>
          <Step>
            <StepLabel>Role</StepLabel>
          </Step>
          <Step>
            <StepLabel>Récapitulatif</StepLabel>
          </Step>
        </Stepper>
        <Divider className={classes.divider}/>
        {getStepContent()}
      </DialogContent>
      <DialogActions>
        {stepIndex === 2 ?
          (
            <Button onClick={() => {
              setSubscriptionSkipped(true);
              setStepIndex((prevIdx: number) => prevIdx + 1)
            }}>
              Passer
            </Button>
          ) : (<></>)
        }
        <Button
          onClick={handlePrev}
          disabled={stepIndex < 1 || stepIndex > 3}
        >
          Précédent
        </Button>
        <Button
          onClick={handleNext}
          variant="contained"
          disabled={stepIndex > 3 || (stepIndex === 0 && user === null) || (stepIndex === 1 && amount.length === 0) || (stepIndex === 2 && (!subscription || subscription.role.length === 0 ))}
        >
          {stepIndex < 3 ? 'Suivant' : 'Terminer'}
        </Button>
        {stepIndex === 4 ?
          (
            <CircularProgress />
          ) : (<></>)
        }
      </DialogActions>
    </Dialog>
  )
}